import { create } from "zustand";

/**
 * Named visual styles ("skins"), each a complete look: full token palette + font
 * stacks + light/dark color-scheme. Code-named so we can refer to them while
 * choosing the final one. Applied by writing CSS variables on <html>, which all
 * components consume via Tailwind's semantic colors.
 */

export interface StyleTokens {
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  text: string;
  muted: string;
  primary: string;
  primaryFg: string;
  accent: string;
}

export interface VisualStyle {
  id: string;
  /** Code name shown in the picker. */
  name: string;
  tagline: string;
  scheme: "dark" | "light";
  fontSans: string;
  fontDisplay: string;
  tokens: StyleTokens;
}

const SANS = "Inter, 'Segoe UI', system-ui, sans-serif";
const SERIF = "Georgia, 'Times New Roman', serif";
const MONO = "'Cascadia Code', 'Consolas', ui-monospace, monospace";

export const STYLES: VisualStyle[] = [
  {
    id: "nebula",
    name: "NEBULA",
    tagline: "Deep space violet — the default.",
    scheme: "dark",
    fontSans: SANS,
    fontDisplay: SANS,
    tokens: {
      bg: "13 17 23", surface: "22 27 34", surface2: "30 37 46", border: "48 54 61",
      text: "230 237 243", muted: "139 148 158", primary: "124 92 255",
      primaryFg: "255 255 255", accent: "56 189 248",
    },
  },
  {
    id: "aurora",
    name: "AURORA",
    tagline: "Teal night with emerald light.",
    scheme: "dark",
    fontSans: SANS,
    fontDisplay: SANS,
    tokens: {
      bg: "8 20 22", surface: "14 30 32", surface2: "20 42 44", border: "35 60 60",
      text: "224 242 241", muted: "125 160 158", primary: "16 185 129",
      primaryFg: "4 20 16", accent: "45 212 191",
    },
  },
  {
    id: "ember",
    name: "EMBER",
    tagline: "Warm charcoal and molten orange.",
    scheme: "dark",
    fontSans: SANS,
    fontDisplay: SANS,
    tokens: {
      bg: "23 17 14", surface: "33 25 21", surface2: "45 34 28", border: "64 48 40",
      text: "245 235 228", muted: "168 150 138", primary: "249 115 22",
      primaryFg: "28 16 8", accent: "250 204 21",
    },
  },
  {
    id: "synthwave",
    name: "SYNTHWAVE",
    tagline: "Indigo nights, neon magenta & cyan.",
    scheme: "dark",
    fontSans: SANS,
    fontDisplay: SANS,
    tokens: {
      bg: "16 12 32", surface: "26 20 48", surface2: "38 28 66", border: "64 48 102",
      text: "240 232 255", muted: "168 150 200", primary: "217 70 239",
      primaryFg: "255 255 255", accent: "34 211 238",
    },
  },
  {
    id: "terminal",
    name: "TERMINAL",
    tagline: "Green phosphor on black, monospace.",
    scheme: "dark",
    fontSans: MONO,
    fontDisplay: MONO,
    tokens: {
      bg: "5 8 6", surface: "10 16 12", surface2: "16 24 18", border: "30 46 34",
      text: "190 255 200", muted: "110 160 120", primary: "34 197 94",
      primaryFg: "5 8 6", accent: "132 255 160",
    },
  },
  {
    id: "daylight",
    name: "DAYLIGHT",
    tagline: "Clean, bright, and modern.",
    scheme: "light",
    fontSans: SANS,
    fontDisplay: SANS,
    tokens: {
      bg: "248 250 252", surface: "255 255 255", surface2: "241 245 249", border: "226 232 240",
      text: "15 23 42", muted: "100 116 139", primary: "37 99 235",
      primaryFg: "255 255 255", accent: "14 165 233",
    },
  },
  {
    id: "parchment",
    name: "PARCHMENT",
    tagline: "Warm paper and ink — booky & calm.",
    scheme: "light",
    fontSans: SERIF,
    fontDisplay: SERIF,
    tokens: {
      bg: "244 238 224", surface: "252 248 238", surface2: "236 228 210", border: "214 202 178",
      text: "56 46 33", muted: "122 108 88", primary: "161 98 7",
      primaryFg: "252 248 238", accent: "180 83 9",
    },
  },
];

export const STYLE_BY_ID: Record<string, VisualStyle> = Object.fromEntries(
  STYLES.map((s) => [s.id, s])
);

const STORAGE_KEY = "hippo.style";

function getInitialId(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved && STYLE_BY_ID[saved] ? saved : "nebula";
}

function applyStyle(id: string) {
  const s = STYLE_BY_ID[id] ?? STYLES[0];
  const root = document.documentElement;
  const t = s.tokens;
  root.style.setProperty("--bg", t.bg);
  root.style.setProperty("--surface", t.surface);
  root.style.setProperty("--surface-2", t.surface2);
  root.style.setProperty("--border", t.border);
  root.style.setProperty("--text", t.text);
  root.style.setProperty("--muted", t.muted);
  root.style.setProperty("--primary", t.primary);
  root.style.setProperty("--primary-fg", t.primaryFg);
  root.style.setProperty("--accent", t.accent);
  root.style.setProperty("--font-sans", s.fontSans);
  root.style.setProperty("--font-display", s.fontDisplay);
  root.style.colorScheme = s.scheme;
}

interface StyleState {
  styleId: string;
  setStyle: (id: string) => void;
  cycleStyle: () => void;
}

export const useStyleStore = create<StyleState>((set, get) => ({
  styleId: getInitialId(),
  setStyle: (id) => {
    applyStyle(id);
    localStorage.setItem(STORAGE_KEY, id);
    set({ styleId: id });
  },
  cycleStyle: () => {
    const idx = STYLES.findIndex((s) => s.id === get().styleId);
    const next = STYLES[(idx + 1) % STYLES.length];
    get().setStyle(next.id);
  },
}));

// Apply the persisted style immediately on load (before first paint).
applyStyle(getInitialId());
